import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './QuoteModal.css';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (quoteData: QuoteData) => void;
  loading?: boolean;
}

export interface QuoteData {
  title: string;
  description: string;
  price: number;
  deliveryDays: number;
  revisions: number;
}

const QuoteModal: React.FC<QuoteModalProps> = ({ isOpen, onClose, onSubmit, loading }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [revisions, setRevisions] = useState('1');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !price || !deliveryDays) {
      alert(t('quoteModal.requiredFields'));
      return;
    }

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      price: parseInt(price),
      deliveryDays: parseInt(deliveryDays),
      revisions: parseInt(revisions) || 1
    });
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setDeliveryDays('');
    setRevisions('1');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={handleClose}>
      <div className="quote-modal" onClick={e => e.stopPropagation()}>
        <div className="quote-modal-header">
          <h2>💰 {t('quoteModal.title')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('quoteModal.workTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t('quoteModal.titlePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('quoteModal.workDesc')}</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('quoteModal.descPlaceholder')}
              rows={4}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('quoteModal.priceLabel')}</label>
              <input
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="100000"
                min="1000"
                required
              />
            </div>

            <div className="form-group">
              <label>{t('quoteModal.daysLabel')}</label>
              <input
                type="number"
                value={deliveryDays}
                onChange={e => setDeliveryDays(e.target.value)}
                placeholder="7"
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label>{t('quoteModal.revisionsLabel')}</label>
              <input
                type="number"
                value={revisions}
                onChange={e => setRevisions(e.target.value)}
                placeholder="1"
                min="0"
              />
            </div>
          </div>

          {price && (
            <div className="quote-preview">
              <h4>{t('quoteModal.preview')}</h4>
              <div className="preview-item">
                <span>{t('quoteModal.price')}</span>
                <strong>{t('projects.budgetExact', { v: parseInt(price).toLocaleString() })}</strong>
              </div>
              <div className="preview-item">
                <span>{t('quoteModal.days')}</span>
                <strong>{t('projectDetail.daysN', { n: deliveryDays || 0 })}</strong>
              </div>
              <div className="preview-item">
                <span>{t('quoteModal.revisionsLabel')}</span>
                <strong>{t('serviceDetail.timesN', { n: revisions || 1 })}</strong>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={handleClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? t('projectDetail.sending') : t('quoteModal.title')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;
